import { useState, useCallback, useRef } from 'react'
import { API_BASE } from '../lib/api'
import { useStore } from '../lib/store'

async function tryRefresh(): Promise<boolean> {
  try {
    const r = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',  // Send the HttpOnly sb_refresh cookie
    })
    if (!r.ok) return false
    const data = await r.json()
    if (!data.access_token) return false
    useStore.getState().setAccessToken(data.access_token)
    return true
  } catch {
    return false
  }
}

export type MessageRole = 'user' | 'assistant'

export interface ChatMessage {
  id: string
  role: MessageRole
  content: string
  toolCalls?: ToolEvent[]
  isStreaming?: boolean
  generationState?: 'streaming' | 'completed' | 'stopped' | 'failed'
  timestamp: Date
}

export interface ToolEvent {
  type: 'tool_call' | 'tool_result'
  name: string
  input?: Record<string, unknown>
  result?: Record<string, unknown>
  started_at?: number
  completed_at?: number
  duration_ms?: number
}

interface UseChatStreamOptions {
  conversationId: string
  onConfigUpdate?: (status: string) => void
  onResponseComplete?: () => void
}

export function useChatStream({ conversationId, onConfigUpdate, onResponseComplete }: UseChatStreamOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [activeTools, setActiveTools] = useState<ToolEvent[]>([])
  const abortRef = useRef<AbortController | null>(null)

  // Streaming text buffer — accumulates chunks between animation frames.
  // We write here on every SSE event but only call setMessages() once per
  // requestAnimationFrame (~16 ms / 60 fps) to avoid thrashing React's
  // reconciler with hundreds of micro-renders during long responses.
  const streamTextRef = useRef('')
  const rafIdRef = useRef<number>(0)      // handle returned by requestAnimationFrame
  const rafPendingRef = useRef(false)     // true only while a RAF callback is queued

  const sendMessage = useCallback(async (
    content: string,
    options: {
      customerApiDoc?: string
      customerApiDocType?: string
      endpointsFilterData?: Record<string, any>
      schemaUserImportData?: Record<string, any>
      onAccepted?: () => void
    } = {}
  ) => {
    if (isLoading) return

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date()
    }
    setMessages(prev => [...prev, userMsg])
    setIsLoading(true)
    setActiveTools([])

    // Placeholder assistant message for streaming
    const assistantId = crypto.randomUUID()
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      toolCalls: [],
      isStreaming: true,
      generationState: 'streaming',
      timestamp: new Date()
    }
    setMessages(prev => [...prev, assistantMsg])

    abortRef.current = new AbortController()

    const doFetch = () => {
      const token = useStore.getState().accessToken
      return fetch(`${API_BASE}/api/conversations/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          content,
          customer_api_doc: options.customerApiDoc,
          customer_api_doc_type: options.customerApiDocType,
          endpoints_filter_data: options.endpointsFilterData,
          schema_user_import_data: options.schemaUserImportData
        }),
        signal: abortRef.current!.signal
      })
    }

    try {
      let response = await doFetch()

      // On 401 try to refresh the access token via HttpOnly cookie, then retry once
      if (response.status === 401) {
        const refreshed = await tryRefresh()
        if (refreshed) {
          response = await doFetch()
        } else {
          window.dispatchEvent(new CustomEvent('auth:session-expired'))
          setMessages(prev => prev.map(m =>
            m.id === assistantId
              ? { ...m, content: 'Your session expired before the response could start.', isStreaming: false, generationState: 'failed' }
              : m
          ))
          setIsLoading(false)
          return
        }
      }

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After')
        let retryMsg = "You've reached the hourly message limit."
        if (retryAfter) {
          const seconds = parseInt(retryAfter, 10)
          if (!isNaN(seconds)) {
            const minutes = Math.ceil(seconds / 60)
            retryMsg += ` Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`
          }
        }
        setMessages(prev => prev.map(m =>
          m.id === assistantId
            ? { ...m, content: retryMsg, isStreaming: false, generationState: 'failed' }
            : m
        ))
        setIsLoading(false)
        return
      }

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`)

      if (!response.body) throw new Error('No response body')
      options.onAccepted?.()

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      const toolCallLog: ToolEvent[] = []
      let streamCompleted = false

      // Reset per-stream state
      streamTextRef.current = ''
      rafPendingRef.current = false

      // Schedule a single RAF to flush buffered text to React state.
      // If a flush is already pending for this frame, do nothing — the
      // already-scheduled callback will pick up the latest ref value.
      const scheduleFlush = () => {
        if (rafPendingRef.current) return
        rafPendingRef.current = true
        rafIdRef.current = requestAnimationFrame(() => {
          rafPendingRef.current = false
          rafIdRef.current = 0
          const text = streamTextRef.current
          setMessages(prev => prev.map(m =>
            m.id === assistantId ? { ...m, content: text } : m
          ))
        })
      }

      // Cancel any pending RAF so stale buffered text can't overwrite a
      // finalised message state (done, error, or user cancel).
      const cancelPendingRaf = () => {
        if (rafPendingRef.current) {
          cancelAnimationFrame(rafIdRef.current)
          rafIdRef.current = 0
          rafPendingRef.current = false
        }
      }

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (!data) continue

          try {
            const event = JSON.parse(data)

            if (event.type === 'text') {
              // Write to ref only — a RAF will flush it to state at most 60×/sec
              streamTextRef.current += event.content
              scheduleFlush()
            }

            else if (event.type === 'tool_call') {
              const toolCall: ToolEvent = {
                type: 'tool_call',
                name: event.name,
                input: event.input,
                started_at: event.started_at,
              }
              toolCallLog.push(toolCall)
              setActiveTools(prev => [...prev, toolCall])
            }

            else if (event.type === 'tool_result') {
              // Match to existing tool call and update
              setActiveTools(prev => {
                const updated = [...prev]
                const last = updated.filter(t => t.name === event.name && !t.result).pop()
                if (last) {
                  last.result = event.result
                  last.completed_at = event.completed_at
                  last.duration_ms = event.duration_ms
                }
                return updated
              })
              const logged = [...toolCallLog].reverse().find(t => t.name === event.name && !t.result)
              if (logged) {
                logged.result = event.result
                logged.completed_at = event.completed_at
                logged.duration_ms = event.duration_ms
              }
            }

            else if (event.type === 'config_update') {
              onConfigUpdate?.(event.status)
            }

            else if (event.type === 'done') {
              streamCompleted = true
              // Cancel any pending RAF and do one final synchronous flush so
              // the complete text is committed before isStreaming flips false.
              cancelPendingRaf()
              const completedTools: ToolEvent[] = Array.isArray(event.tool_calls)
                ? event.tool_calls.map((tool: Omit<ToolEvent, 'type'>) => ({ ...tool, type: 'tool_call' as const }))
                : toolCallLog
              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? {
                      ...m,
                      id: event.message_id || m.id,
                      content: streamTextRef.current,
                      isStreaming: false,
                      generationState: 'completed',
                      toolCalls: completedTools,
                    }
                  : m
              ))
              setIsLoading(false)
              setActiveTools([])
              onResponseComplete?.()
            }

            else if (event.type === 'error') {
              streamCompleted = true
              cancelPendingRaf()
              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? { ...m, content: `Error: ${event.message}`, isStreaming: false, generationState: 'failed' }
                  : m
              ))
              setIsLoading(false)
              setActiveTools([])
            }
          } catch {
            // Skip malformed SSE lines
          }
        }
      }
      if (!streamCompleted && !abortRef.current?.signal.aborted) {
        cancelPendingRaf()
        setMessages(prev => prev.map(m =>
          m.id === assistantId
            ? {
                ...m,
                content: streamTextRef.current || 'The response ended before completion.',
                isStreaming: false,
                generationState: 'failed',
                toolCalls: toolCallLog,
              }
            : m
        ))
        setIsLoading(false)
        setActiveTools([])
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return
      setMessages(prev => prev.map(m =>
        m.id === assistantId
          ? { ...m, content: 'Connection error. Please try again.', isStreaming: false, generationState: 'failed' }
          : m
      ))
      setIsLoading(false)
      setActiveTools([])
    }
  }, [conversationId, isLoading, onConfigUpdate, onResponseComplete])

  const cancelStream = useCallback(() => {
    abortRef.current?.abort()
    if (rafPendingRef.current) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = 0
      rafPendingRef.current = false
    }
    setIsLoading(false)
    // Flush whatever text was buffered so the message shows up to the cancel point
    setMessages(prev => prev.map(m =>
      m.isStreaming
        ? { ...m, content: streamTextRef.current || m.content, isStreaming: false, generationState: 'stopped' }
        : m
    ))
    setActiveTools([])
  }, [])

  const loadInitialHistory = useCallback((history: ChatMessage[]) => {
    setMessages(history)
  }, [])

  const prependHistory = useCallback((history: ChatMessage[]) => {
    setMessages(current => {
      const seen = new Set(current.map(message => message.id))
      const uniqueOlder = history.filter(message => {
        if (seen.has(message.id)) return false
        seen.add(message.id)
        return true
      })
      return uniqueOlder.length ? [...uniqueOlder, ...current] : current
    })
  }, [])

  return {
    messages,
    isLoading,
    activeTools,
    sendMessage,
    cancelStream,
    loadInitialHistory,
    prependHistory,
  }
}
